import React from "react";
import { Image, message, Modal } from "antd";
import { Menu, Settings, Unlink } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useUnlinkPhysicalButton } from "@/hooks/useUsers";
import { PlusOutlined, ExclamationCircleOutlined } from "@ant-design/icons";

interface PhysicalButtonCardProps {
  deviceName?: string;
  dateAdded?: string;
  onEdit?: () => void;
  userId: number;
  refetchButtons: () => void;

  buttonId: string;
}

const PhysicalButtonCard: React.FC<PhysicalButtonCardProps> = ({
  deviceName,
  dateAdded,
  onEdit,
  refetchButtons,
  userId,
  buttonId,
}) => {
  const unlinkPhysicalButton = useUnlinkPhysicalButton();

  const handleUnlinkButton = async () => {
    Modal.confirm({
      title: (
        <div className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
              <ExclamationCircleOutlined className="text-red-500 text-4xl" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Warning</h2>
        </div>
      ),
      icon: null, // Remove default icon since we're using custom one
      content: (
        <div className="text-center">
          <p className="text-gray-600 mb-6">
            You are about to unlink this button. Once unlinked, it will no
            longer be able to trigger a call-out
          </p>
        </div>
      ),
      okText: "Confirm Unlink",
      okType: "danger",
      cancelText: "Close",
      centered: true,
      width: 450,
      className: "custom-unlink-modal",
      okButtonProps: {
        className: "bg-red-600 text-white border-red-500  px-6 py-2 h-auto",
        size: "large",
      },
      cancelButtonProps: {
        className: "border-blue-600 bg-white text-blue-600 px-6 py-2 h-auto",
        size: "large",
      },
      async onOk() {
        try {
          const payload: IUnlinkPhysicalButton = {
            buttonId: buttonId.toString(),
            userId: userId,
            platform: "console",
          };

          await unlinkPhysicalButton.mutateAsync(payload);

          message.success("Physical button unlinked successfully!");
          refetchButtons();
        } catch (error: any) {
          const statusCode = error?.response?.status;
          const errorMessage = error?.response?.data?.message || error?.message;

          switch (statusCode) {
            case 400:
              message.error(
                `Invalid data: ${
                  errorMessage || "Please check the button details"
                }`
              );
              break;
            case 401:
              message.error("Unauthorized: Please log in again");
              break;
            case 403:
              message.error(
                "Forbidden: You don't have permission to unlink this button"
              );
              break;
            case 404:
              message.error(
                "Button not found: It may have already been unlinked"
              );
              break;
            case 409:
              message.error(
                `Conflict: ${
                  errorMessage || "This button cannot be unlinked at this time"
                }`
              );
              break;
            case 500:
              message.error("Server error: Please try again later");
              break;
            default:
              message.error(
                `Error unlinking button: ${errorMessage || "Please try again"}`
              );
          }
        }
      },
    });
  };

  return (
    <div className="rounded-lg p-4 bg-popupColor relative">
      <div className="flex items-start gap-4">
        <div className="w-16 h-16 rounded-md flex items-start justify-start">
          <Image
            src={require("../../../assets/images/buttonRemote.png")}
            alt="Device Icon"
            height="60px"
            width="65px"
            preview={false}
          />
        </div>

        {/* Device Info - Column layout */}
        <div className="flex flex-col  items-start justify-start">
          <h3 className="text-base  font-semibold text-gray-900 mb-1">
            {deviceName}
          </h3>
          <p className="text-sm text-gray-500">Added: {dateAdded}</p>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild className="flex items-end justify-end">
            <button
              type="button"
              className="flex items-start justify-end h-6 hover:border-neutral-800 transition-colors"
            >
              <Menu className="flex items-start justify-end w-full h-full hover:bg-gray-700/10 rounded" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-48 text-xs font-medium text-black space-y-[2px]">
            <DropdownMenuLabel>Options</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="py-2 text-sm cursor-pointer"
              onClick={onEdit}
            >
              <Settings className="h-4 mr-2" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="py-2 text-sm cursor-pointer text-red-600 hover:text-red-700"
              onClick={handleUnlinkButton}
            >
              <Unlink className="h-4 mr-2" />
              Unlink Button
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default PhysicalButtonCard;
